import { FileDownload, Paid, BorderColor, Key, InsertDriveFile } from '@mui/icons-material';
import { Box, MenuItem, Menu } from '@mui/material';
import { useTranslate } from 'ra-core';
import { useListContext, Button } from 'react-admin';
import { useCallback, useState } from 'react';
import JSZip from 'jszip';

import { transactionsDataProvider } from '@/dataProvider';
import { ITransactionsResponseData } from '@/types';

type ExportFilesType = 'transactions' | 'pdf' | 'signed_orders' | 'not_signed_orders';

function ExportTransactionsButton(): JSX.Element {
  const t = useTranslate();
  const { filterValues, perPage, page, sort } = useListContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const isMenuOpen = Boolean(anchorEl);

  const downloadTransactions = async (): Promise<void> => {
    const response = await transactionsDataProvider.exportTransactions({
      filter: {
        status: filterValues?.status,
        endDate: filterValues?.endDate,
        startDate: filterValues?.startDate,
        full_name: filterValues?.full_name,
        companies: filterValues?.companies,
        withdrawalOrderStatus: filterValues?.withdrawalOrderStatus,
      },
      pagination: {
        page,
        perPage,
      },
      sort,
    });

    if (response) {
      const url = response;
      const link = document.createElement('a');
      link.href = url;
      // link.setAttribute('download', 'transactions.xls');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    }
  };

  const getAllListData = async (): Promise<ITransactionsResponseData[]> => {
    const list: ITransactionsResponseData[] = [];

    let hasNextPage = false;
    let page = 1;

    do {
      const response = await transactionsDataProvider.getList({
        pagination: { page, perPage: 50 },
        sort,
        filter: filterValues,
      });

      if (response.data?.length) {
        list.push(...response.data);
      }

      hasNextPage = response.pageInfo?.hasNextPage || false;
      page++;
    } while (hasNextPage);

    return list;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClick = useCallback(
    async (type: ExportFilesType) => {
      setIsButtonEnabled(false);

      handleMenuClose();

      if (type === 'transactions') {
        await downloadTransactions();
      } else {
        const list = await getAllListData();

        const archiveFileName =
          (type === 'pdf' && 'Заяви') ||
          (type === 'signed_orders' && 'Підписані накази') ||
          (type === 'not_signed_orders' && 'Не підписані накази') ||
          `archive`;

        const links = list
          .filter(
            item =>
              (type === 'pdf' && item.application_link) ||
              (type === 'signed_orders' && item.signed_order_link) ||
              (type === 'not_signed_orders' &&
                item.withdrawal_order_link &&
                !item.signed_order_link),
          )
          .map(({ application_link, signed_order_link, withdrawal_order_link }) => {
            let link: string | null;

            switch (type) {
              case 'signed_orders':
                link = signed_order_link;
                break;

              case 'not_signed_orders':
                link = withdrawal_order_link;
                break;

              default:
                link = application_link;
                break;
            }

            if (!link) {
              return;
            }

            const fileName = link.split('/')?.pop() || 'document.pdf';

            return { link, fileName };
          })
          .filter(item => !!item);

        const zip = new JSZip();

        const promises = links.map(async item => {
          if (!item) {
            return;
          }

          try {
            const { link, fileName } = item;
            const fileData = await fetch(link);
            const blob = await fileData.blob();

            zip.file(fileName, blob);
          } catch (error) {
            console.warn(`Unable to pack file to archive: ${error}`);
          }
        });

        await Promise.all(promises);

        if (Object.keys(zip.files).length) {
          const zippedFile = await zip.generateAsync({ type: 'blob' });

          const blob = new Blob([zippedFile], { type: 'application/zip' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${archiveFileName}.zip`;
          link.click();
        }
      }

      setIsButtonEnabled(true);
    },
    [handleMenuClose],
  );

  return (
    <Box>
      <div>
        <Button
          id="basic-button"
          startIcon={<FileDownload />}
          aria-controls={isMenuOpen ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
          onClick={handleMenuOpen}
          label="resources.transactions.download_files"
          disabled={!isButtonEnabled}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem onClick={() => handleClick('transactions')}>
            <Paid fontSize="small" />
            {' '}
            <div>{t('resources.transactions.download_files_transactions')}</div>
          </MenuItem>
          <MenuItem onClick={() => handleClick('pdf')}>
            <BorderColor fontSize="small" />
            {' '}
            <div>{t('resources.transactions.download_files_pdf')}</div>
          </MenuItem>
          <MenuItem onClick={() => handleClick('signed_orders')}>
            <Key fontSize="small" />
            {' '}
            <div>{t('resources.transactions.download_files_signed_orders')}</div>
          </MenuItem>
          <MenuItem onClick={() => handleClick('not_signed_orders')}>
            <InsertDriveFile fontSize="small" />
            {' '}
            <div>{t('resources.transactions.download_files_not_signed_orders')}</div>
          </MenuItem>
        </Menu>
      </div>
    </Box>
  );
}

export default ExportTransactionsButton;
